import React, { useState } from 'react';
import {
  Brain, ArrowLeft, ArrowRight, School, Users, MessageSquare, Calendar,
  FileText, Bell, CheckCircle2, Sparkles, Settings, BarChart3,
  ChevronDown, ChevronUp, BookOpen, Zap, Shield, Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface StepProps {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  details: React.ReactNode;
  tip?: string;
}

function Step({ number, title, description, icon, details, tip }: StepProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-surface-light rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-6 text-left"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold text-lg">
            {number}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              {icon}
              <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
            </div>
            <p className="text-text-secondary text-sm">{description}</p>
          </div>
          <div className="flex-shrink-0 ml-2">
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-primary-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-text-muted" />
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-6 pb-6 border-t border-border-light">
          <div className="pt-4 pl-14 space-y-3">
            {details}
            {tip && (
              <div className="mt-4 flex items-start gap-2 bg-accent-50 rounded-lg p-3">
                <Sparkles className="w-4 h-4 text-accent-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-accent-700 font-medium">{tip}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Checklist({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-success-500 flex-shrink-0 mt-0.5" />
          <span className="text-text-secondary text-sm">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function GettingStartedPage() {
  useDocumentTitle('Getting Started');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark">
      {/* Navigation */}
      <nav className="border-b border-border-light bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-text-primary">Campus Connect</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/support" className="text-text-secondary hover:text-primary-500 transition-colors text-sm">
                Support
              </Link>
              <Link to="/" className="flex items-center gap-2 text-text-secondary hover:text-primary-500 transition-colors text-sm">
                <ArrowLeft className="w-4 h-4" />
                Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center">
              <BookOpen className="w-9 h-9 text-white" />
            </div>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-text-primary mb-3">
            Getting Started with CampusConnect
          </h1>
          <p className="text-lg text-text-secondary max-w-xl mx-auto">
            Set up your school in under 10 minutes. Follow these 7 steps to get your
            entire team connected and productive.
          </p>
        </div>

        {/* Time Estimate Bar */}
        <div className="flex items-center justify-center gap-6 mb-10 bg-surface-light rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary-500" />
            <span className="text-sm font-medium text-text-primary">Setup time: ~10 minutes</span>
          </div>
          <div className="w-px h-5 bg-border-light" />
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-success-500" />
            <span className="text-sm font-medium text-text-primary">Free plan available</span>
          </div>
          <div className="w-px h-5 bg-border-light hidden sm:block" />
          <div className="hidden sm:flex items-center gap-2">
            <Zap className="w-5 h-5 text-accent-500" />
            <span className="text-sm font-medium text-text-primary">No credit card required</span>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          <Step
            number={1}
            title="Create Your Account"
            description="Sign up with your school email to get started."
            icon={<Brain className="w-5 h-5 text-primary-500" />}
            tip="Use your school email address — it makes it easier for colleagues to find and join your school."
            details={
              <div className="space-y-3">
                <Checklist items={[
                  'Go to campusconnectedu.com and click "Start Free Trial"',
                  'Enter your name, school email, and create a password',
                  'Verify your email address by clicking the link we send you',
                  'You\'ll land on the onboarding tour — take 60 seconds to see what\'s possible',
                ]} />
                <p className="text-text-muted text-xs">
                  Your Free plan includes up to 50 users, AI dashboard, messaging, calendar, documents, and notifications — no credit card needed.
                </p>
              </div>
            }
          />

          <Step
            number={2}
            title="Set Up Your School"
            description="Create your school profile so your team can join."
            icon={<School className="w-5 h-5 text-secondary-500" />}
            tip="Choose your school name carefully — your team will see this when they receive their invitation."
            details={
              <div className="space-y-3">
                <Checklist items={[
                  'After onboarding, click "Create a School" on the dashboard',
                  'Enter your school name (e.g., "Riverside Christian Academy")',
                  'Select your school type: K-12, Academy, or College',
                  'Add a short description (optional but helpful for new team members)',
                  'Your school is created! You\'re automatically the administrator',
                ]} />
                <div className="bg-primary-50 rounded-lg p-3">
                  <p className="text-xs text-primary-700">
                    <strong>Already have a school on CampusConnect?</strong> Ask your administrator for an invite.
                    Go to the dashboard and click "Join a School" — enter the invite code they share with you.
                  </p>
                </div>
              </div>
            }
          />

          <Step
            number={3}
            title="Invite Your Team"
            description="Add teachers, staff, and other admins to your school."
            icon={<Users className="w-5 h-5 text-secondary-500" />}
            tip="Start small — invite 2-3 key colleagues first. Once they're comfortable, they'll help spread the word to the rest of the team."
            details={
              <div className="space-y-3">
                <Checklist items={[
                  'Go to the Team page from the sidebar',
                  'Click "Invite Member" and enter their email address',
                  'Choose their role: Admin (full access), Teacher, or Staff',
                  'They\'ll get an email with a link to join your school',
                  'Track pending invitations on the Team page',
                ]} />
                <div className="bg-secondary-50 rounded-lg p-3">
                  <p className="text-xs text-secondary-700">
                    <strong>Roles explained:</strong><br />
                    <strong>Admin</strong> — Full access to settings, billing, team management, and all data<br />
                    <strong>Teacher</strong> — Access to classroom features, messaging, calendar, documents, and tasks<br />
                    <strong>Staff</strong> — Access to messaging, calendar, and documents relevant to their role
                  </p>
                </div>
              </div>
            }
          />

          <Step
            number={4}
            title="Start Communicating"
            description="Set up channels for your teams and departments."
            icon={<MessageSquare className="w-5 h-5 text-primary-500" />}
            tip="Create a #general channel for all-school announcements, and separate channels for departments like #math-dept, #admin-team, or #field-trips."
            details={
              <div className="space-y-3">
                <Checklist items={[
                  'Go to Messages from the sidebar',
                  'Click "Create Channel" to start a group conversation',
                  'Name it descriptively (e.g., "4th Grade Teachers", "Front Office")',
                  'Add team members who should be in the channel',
                  'Send your first message — your team gets notified instantly',
                  'Use Direct Messages for private 1-on-1 conversations',
                ]} />
                <div className="bg-accent-50 rounded-lg p-3">
                  <p className="text-xs text-accent-700">
                    <strong>AI Feature:</strong> Long message threads? Click the AI Summary button to get an
                    instant recap of the conversation. Great for catching up after being out.
                  </p>
                </div>
              </div>
            }
          />

          <Step
            number={5}
            title="Organize Your Calendar"
            description="Create events, set reminders, and sync with Google Calendar."
            icon={<Calendar className="w-5 h-5 text-accent-500" />}
            tip="Connect your Google Calendar in Settings > Calendar to keep everything in sync — events created in either place show up in both."
            details={
              <div className="space-y-3">
                <Checklist items={[
                  'Go to Calendar from the sidebar',
                  'Click any date to create a new event',
                  'Set the title, time, description, and choose who should be notified',
                  'Events appear on everyone\'s calendar automatically',
                  'Optional: Connect Google Calendar in Settings > Calendar for two-way sync',
                ]} />
                <p className="text-text-muted text-xs">
                  The calendar is shared across your school. Great for staff meetings, parent-teacher conferences,
                  field trips, professional development days, and deadlines.
                </p>
              </div>
            }
          />

          <Step
            number={6}
            title="Share Documents"
            description="Upload and organize files your team needs access to."
            icon={<FileText className="w-5 h-5 text-secondary-500" />}
            details={
              <div className="space-y-3">
                <Checklist items={[
                  'Go to Documents from the sidebar',
                  'Click "Upload" to add files (PDFs, images, spreadsheets, etc.)',
                  'Create folders to keep things organized (e.g., "Policies", "Forms", "Curriculum")',
                  'Set permissions — choose who can view or edit each document',
                  'Team members can search and access shared documents anytime',
                ]} />
                <p className="text-text-muted text-xs">
                  Documents are stored securely in the cloud. No more emailing files back and forth or
                  losing track of the latest version.
                </p>
              </div>
            }
          />

          <Step
            number={7}
            title="Set Up Notifications"
            description="Make sure you never miss important updates."
            icon={<Bell className="w-5 h-5 text-accent-500" />}
            tip="Enable push notifications on your phone browser for real-time alerts — add CampusConnect to your home screen for the best mobile experience."
            details={
              <div className="space-y-3">
                <Checklist items={[
                  'When prompted, allow push notifications in your browser',
                  'Go to Settings > Notifications to customize what you get notified about',
                  'Choose alerts for: new messages, calendar events, task assignments, and more',
                  'On mobile? Add CampusConnect to your home screen for instant access',
                ]} />
                <div className="bg-primary-50 rounded-lg p-3">
                  <p className="text-xs text-primary-700">
                    <strong>Mobile tip:</strong> On iPhone, open campusconnectedu.com in Safari, tap the share icon,
                    and select "Add to Home Screen." On Android, Chrome will prompt you to install the app automatically.
                  </p>
                </div>
              </div>
            }
          />
        </div>

        {/* Bonus: Power Features */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
            Power Features to Explore
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-surface-light rounded-lg shadow-sm p-5">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-primary-500" />
                <h3 className="font-semibold text-text-primary text-sm">Attendance Tracking</h3>
              </div>
              <p className="text-xs text-text-secondary">
                Track student attendance digitally. Mark present, absent, tardy, or excused in seconds.
                View patterns and generate reports for the office.
              </p>
            </div>
            <div className="bg-surface-light rounded-lg shadow-sm p-5">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-5 h-5 text-secondary-500" />
                <h3 className="font-semibold text-text-primary text-sm">LMS Integration</h3>
              </div>
              <p className="text-xs text-text-secondary">
                Connect Canvas or Infinite Campus to sync grades, rosters, and assignments automatically.
                Set up in Settings &gt; LMS Integration.
              </p>
            </div>
            <div className="bg-surface-light rounded-lg shadow-sm p-5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-accent-500" />
                <h3 className="font-semibold text-text-primary text-sm">AI Task Automation</h3>
              </div>
              <p className="text-xs text-text-secondary">
                Create tasks and let AI suggest assignments based on roles, workload, and priority. Available on
                Standard and Premium plans.
              </p>
            </div>
            <div className="bg-surface-light rounded-lg shadow-sm p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-success-500" />
                <h3 className="font-semibold text-text-primary text-sm">Custom Branding</h3>
              </div>
              <p className="text-xs text-text-secondary">
                Premium plan schools can customize CampusConnect with their school logo, colors, and branding
                for a personalized experience.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-center text-white">
          <h3 className="text-xl font-bold mb-2">Ready to transform your campus?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-md mx-auto">
            Join schools that save 5+ hours per week on admin work. Your free plan is waiting.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup"
              className="px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2">
              Create Your School
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/support"
              className="px-6 py-3 border-2 border-white/50 text-white rounded-lg hover:bg-white/10 transition-colors">
              Need Help? Contact Support
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-text-muted">
          <div className="flex justify-center gap-6">
            <Link to="/privacy" className="hover:text-primary-500 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary-500 transition-colors">Terms of Service</Link>
            <Link to="/support" className="hover:text-primary-500 transition-colors">Support</Link>
          </div>
        </div>
      </div>
    </div>
  );
}